import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { ChatService } from "@/services/chatService";
import { ChatAttachment, ChatMessage, ChatThread } from "@/types/chat";
import { ROLES } from "@/constants/roles";

interface UseChatOptions {
  cohortId?: string;
  cohortName?: string;
  participantEmail?: string;
  participantName?: string;
  // Messages are only streamed while the window is open; the thread (and so
  // the unread badge) is always watched.
  isOpen?: boolean;
}

/**
 * One participant's chat thread, from either side. The current user's role
 * decides whether they are the "staff" or the "participant" in the thread.
 */
export const useChat = ({
  cohortId,
  cohortName,
  participantEmail,
  participantName,
  isOpen = false,
}: UseChatOptions) => {
  const { currentUser } = useAuth();
  const isStaff =
    currentUser?.role === ROLES.ADMIN || currentUser?.role === ROLES.TRAINER;
  const senderRole = isStaff ? "staff" : "participant";

  const threadId = useMemo(
    () =>
      cohortId && participantEmail
        ? ChatService.buildThreadId(cohortId, participantEmail)
        : "",
    [cohortId, participantEmail],
  );

  const [thread, setThread] = useState<ChatThread | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const markedRef = useRef<string>("");

  useEffect(() => {
    if (!threadId) {
      setThread(null);
      return undefined;
    }
    return ChatService.subscribeToThread(threadId, setThread);
  }, [threadId]);

  useEffect(() => {
    if (!threadId || !isOpen) {
      setMessages([]);
      return undefined;
    }
    setLoadingMessages(true);
    const unsubscribe = ChatService.subscribeToMessages(threadId, (list) => {
      setMessages(list);
      setLoadingMessages(false);
    });
    return unsubscribe;
  }, [threadId, isOpen]);

  const unreadCount = thread
    ? isStaff
      ? thread.unreadForStaff
      : thread.unreadForParticipant
    : 0;

  // Clear this side's badge whenever the window is open and something arrives.
  useEffect(() => {
    if (!threadId || !isOpen || unreadCount === 0) return;
    const key = `${threadId}:${messages.length}`;
    if (markedRef.current === key) return;
    markedRef.current = key;
    void ChatService.markRead(threadId, isStaff ? "staff" : "participant");
  }, [threadId, isOpen, unreadCount, messages.length, isStaff]);

  const sendMessage = useCallback(
    async (text: string, files: File[] = []) => {
      if (!cohortId || !participantEmail || !currentUser) return false;
      if (!text.trim() && files.length === 0) return false;

      setSending(true);
      setError(null);
      try {
        const attachments: ChatAttachment[] = [];
        for (const file of files) {
          attachments.push(await ChatService.uploadAttachment(threadId, file));
        }

        await ChatService.sendMessage({
          cohortId,
          cohortName,
          participantEmail,
          participantName: participantName || "",
          text,
          attachments,
          senderId: currentUser.id || "",
          senderName: currentUser.name || currentUser.email || "",
          senderRole,
        });
        return true;
      } catch (err) {
        console.error("[chat] failed to send message", err);
        setError(
          err instanceof Error
            ? err.message
            : "Your message couldn't be sent. Please try again.",
        );
        return false;
      } finally {
        setSending(false);
      }
    },
    [
      cohortId,
      cohortName,
      participantEmail,
      participantName,
      currentUser,
      senderRole,
      threadId,
    ],
  );

  return {
    threadId,
    thread,
    messages,
    loadingMessages,
    sending,
    error,
    unreadCount,
    isStaff,
    sendMessage,
    clearError: () => setError(null),
  };
};

/** Unread totals for a whole cohort — staff-side badges. */
export const useCohortChatThreads = (cohortId?: string) => {
  const [threads, setThreads] = useState<ChatThread[]>([]);

  useEffect(() => {
    if (!cohortId) {
      setThreads([]);
      return undefined;
    }
    return ChatService.subscribeToCohortThreads(cohortId, setThreads);
  }, [cohortId]);

  const unreadByEmail = useMemo(() => {
    const map = new Map<string, number>();
    threads.forEach((t) => {
      if (t.participantEmail) {
        map.set(t.participantEmail.toLowerCase(), t.unreadForStaff || 0);
      }
    });
    return map;
  }, [threads]);

  // Staff messages the participant hasn't opened yet — the "sent, awaiting
  // read" indicator on the staff side.
  const awaitingReadByEmail = useMemo(() => {
    const map = new Map<string, number>();
    threads.forEach((t) => {
      if (t.participantEmail) {
        map.set(t.participantEmail.toLowerCase(), t.unreadForParticipant || 0);
      }
    });
    return map;
  }, [threads]);

  const totalUnread = useMemo(
    () => threads.reduce((sum, t) => sum + (t.unreadForStaff || 0), 0),
    [threads],
  );

  // Staff messages across the cohort that participants haven't opened yet.
  const totalAwaitingRead = useMemo(
    () => threads.reduce((sum, t) => sum + (t.unreadForParticipant || 0), 0),
    [threads],
  );

  return {
    threads,
    unreadByEmail,
    awaitingReadByEmail,
    totalUnread,
    totalAwaitingRead,
  };
};

export default useChat;
